import { hours } from './utils';
import { heuresParSemaine } from './pointage';
import { tauxTvaEnVigueur } from './tva';
import {
  auCentime,
  compteDansLeCa,
  htDocument,
  signeCa,
  ttcDocument,
  tvaDeLaDepense,
  tvaSaisiePlafonnee,
} from './vat';
import { FRAIS_CAT } from '../constants/accounting';
import { piecesDesDepenses } from './pieces-depenses';

/**
 * Taux horaire retenu quand un pointage renvoie à un employé introuvable.
 *
 * Le cas se présente pour un employé retiré de l'équipe après coup : ses heures
 * restent au chantier, sa fiche n'est plus là. Ignorer ces heures ferait
 * apparaître une marge trop belle ; les compter à un taux moyen approche le coût
 * réel, et le chiffre reste une estimation, comme tout coût de revient calculé
 * en cours de chantier.
 */
const TAUX_HORAIRE_DEFAUT = 70;

/**
 * Coût d'une heure de l'employé pour la marge du chantier.
 *
 * <p>Seul le taux horaire comptait : les heures d'un salarié au mois (taux à
 * 0) et celles du patron sur sa propre fiche ne coûtaient rien, et la marge
 * paraissait trop belle. Un salaire mensuel se ramène à l'heure sur l'horaire
 * du contrat ; une fiche sans salaire ni taux prend le taux moyen.
 *
 * @param {object} [emp] fiche de l'employé
 * @returns {number} coût d'une heure, hors charges
 */
export function coutHoraire(emp) {
  if (!emp) return TAUX_HORAIRE_DEFAUT;
  if (+emp.txH > 0) return +emp.txH;
  const heuresMois = ((+emp.hSem || heuresParSemaine(emp.heuresJour, emp.taux)) * 52) / 12;
  if (+emp.salBase > 0 && heuresMois > 0) return +emp.salBase / heuresMois;
  return TAUX_HORAIRE_DEFAUT;
}

/**
 * Avancement d'après les postes de l'offre cochés dans les rapports.
 *
 * @param {object} ch chantier, avec `postes` et `rapports`
 * @returns {{faits: string[], total: number, pct: number}} postes réalisés et part en %
 */
export function avancementDesPostes(ch) {
  const postes = ch?.postes || [];
  const coches = new Set();
  (ch?.rapports || []).forEach((r) =>
    String(r.contenu ?? r.texte ?? '')
      .split('\n')
      .forEach((l) => {
        const t = l.trim();
        if (t.startsWith('✔ ')) coches.add(t.slice(2).trim());
      }),
  );
  const faits = postes.filter((p) => coches.has(String(p).trim()));
  return {
    faits,
    total: postes.length,
    pct: postes.length ? Math.round((faits.length / postes.length) * 100) : 0,
  };
}

/**
 * Avancement affiché : le réglage du bureau, ou les postes réalisés s'ils vont
 * plus loin. Il ne recule jamais quand un ouvrier coche un poste.
 *
 * @param {object} ch chantier
 * @returns {number} avancement en %
 */
export function avancementDuChantier(ch) {
  return Math.min(100, Math.max(+ch?.av || 0, avancementDesPostes(ch).pct));
}

/**
 * TVA d'une facture de fournisseur du chantier : celle lue sur la facture si
 * elle est saisie, sinon extraite du montant (TTC) au taux de la facture, à
 * défaut au taux normal à sa date. Même règle que la position TVA du serveur.
 *
 * @param {{montant?: number, montantTva?: number, tauxTva?: number, date?: string}} f facture
 * @param {object} [soc] entreprise, pour l'historique des taux
 * @returns {number} la TVA récupérable, au centime
 */
export function tvaFactureFournisseur(f, soc) {
  const montant = +f?.montant || 0;
  const saisie = f?.montantTva;
  if (saisie !== null && saisie !== undefined && saisie !== '' && Number.isFinite(+saisie)) {
    return tvaSaisiePlafonnee(saisie, montant, soc, f?.date);
  }
  const lu = f?.tauxTva;
  const taux =
    lu !== null && lu !== undefined && lu !== '' && Number.isFinite(+lu)
      ? +lu
      : tauxTvaEnVigueur(soc, f?.date).normal;
  if (!taux || !montant) return 0;
  return auCentime(montant - montant / (1 + taux / 100));
}

/**
 * Ce qui a vraiment été facturé au client pour ce chantier : ses factures
 * envoyées (acomptes compris), avoirs déduits.
 *
 * <p>Le montant du chantier est celui de l'offre. Le patron lisait
 * « Facturé 5'341.38 » quand seul l'acompte de 50 % était parti : la TVA du
 * chantier comptait la même erreur.
 *
 * @param {object} ch chantier
 * @param {object[]} [factures] factures clients de la société
 * @returns {{ht: number, tva: number, n: number}} hors taxe, TVA et nombre de factures
 */
export function factureAuClient(ch, factures) {
  const liees = (factures || []).filter(
    (f) =>
      compteDansLeCa(f) &&
      ((f.siteId != null && f.siteId === ch.id) ||
        (ch.devisId != null && f.devisId === ch.devisId)),
  );
  const ht = liees.reduce((s, f) => s + signeCa(f) * htDocument(f), 0);
  const ttc = liees.reduce((s, f) => s + signeCa(f) * ttcDocument(f), 0);
  return { ht: auCentime(ht), tva: auCentime(ttc - ht), n: liees.length };
}

/**
 * Coûts, bénéfice et TVA d'un chantier.
 *
 * <p>Tout se compte hors TVA : le montant facturé du chantier l'est, et la TVA
 * payée sur les achats se récupère auprès de l'État. Les factures des
 * fournisseurs, saisies TTC, comptaient leur TVA comme un coût : la marge en
 * paraissait plus faible. Leur part de TVA va maintenant à la TVA du
 * chantier : celle facturée au client, moins celle payée sur les achats, est
 * ce que le chantier doit à l'État.
 *
 * @param {object} ch chantier
 * @param {object[]} [employes] équipe, pour le taux horaire
 * @param {object} [soc] entreprise, pour les taux de TVA
 * @param {object[]} [facturesClients] factures clients : la TVA facturée est alors celle des
 *   factures réellement envoyées, non celle de l'offre entière
 * @returns {{fac: number, facTtc: number, st: number, mo: number, mat: number, ben: number,
 *   tauxTva: number, tvaFacturee: number, tvaRecuperable: number, tvaDue: number}}
 */
/**
 * Les dépenses (onglet Dépenses, achats de l'ouvrier) rattachées au chantier
 * et absentes de ses factures fournisseurs : même montant et même jour, c'est
 * la même pièce, comptée une fois.
 *
 * @param {object} ch chantier
 * @param {object[]} [frais] dépenses de l'entreprise
 * @returns {object[]} les dépenses à ajouter aux coûts du chantier
 */
export function fraisDuChantier(ch, frais) {
  const dejaLa = new Map();
  (ch.factures || []).forEach((f) => {
    const k = (+f.montant || 0).toFixed(2) + '|' + f.date;
    dejaLa.set(k, (dejaLa.get(k) || 0) + 1);
  });
  return (frais || []).filter((x) => {
    if (x.chantierId == null || String(x.chantierId) !== String(ch.id)) return false;
    const k = (+x.montant || 0).toFixed(2) + '|' + x.date;
    if (dejaLa.get(k) > 0) {
      dejaLa.set(k, dejaLa.get(k) - 1);
      return false;
    }
    return true;
  });
}

export function chantierCosts(ch, employes, soc, facturesClients, frais, achatsJoints) {
  const list = employes || [];
  // Les pièces du chantier, chacune une fois (piecesDesDepenses) : ses
  // factures fournisseurs, les dépenses qui lui sont rattachées (onglet
  // Dépenses, achats de l'ouvrier), la marchandise et la sous-traitance
  // jointes à ses offres et factures, ses sous-traitants pour ce qui n'est
  // pas encore facturé. La même facture jointe à l'offre et à la facture,
  // ou le sous-traitant à son prix convenu et à sa facture, comptaient deux
  // fois dans la marge.
  const rattachees = (frais || []).filter(
    (x) => x.chantierId != null && String(x.chantierId) === String(ch.id),
  );
  const pieces = piecesDesDepenses({
    frais: rattachees,
    chantiers: [ch],
    achatsJoints: (achatsJoints || []).filter((p) => +(p?.piece || p)?.montant > 0),
    chantierId: ch.id,
  });
  // La TVA lue sur la pièce ; à défaut, celle du taux de sa catégorie (une
  // dépense) ou du taux normal (une facture de fournisseur).
  const tvaDe = (g) => {
    // Une dépense sans TVA notée, dont la copie au chantier porte la TVA lue
    // sur la facture : c'est celle-là qui compte.
    const lue =
      g.source === 'frais' && g.item.montantTva == null
        ? g.doublons.find((d) => d.source !== 'frais' && d.item?.montantTva != null)
        : null;
    if (lue) return tvaPiece(lue);
    return tvaPiece(g);
  };
  const tvaPiece = (g) =>
    g.source === 'frais'
      ? tvaDeLaDepense(g.item, FRAIS_CAT, soc)
      : tvaFactureFournisseur(
          g.source === 'chantier'
            ? g.item
            : {
                montant: g.montant,
                date: g.date,
                montantTva: g.source === 'soustraitant' ? g.montantTva : g.item.montantTva,
              },
          soc,
        );
  // Une facture fournisseur, une dépense ou une pièce classée « Sous-traitance »
  // est de la sous-traitance : elle se comptait dans les matériaux.
  const sousTraitance = (g) =>
    g.source === 'soustraitant' ||
    /sous.?trait/i.test(String(g.item.categorie || g.item.cat || g.item.type || g.type || ''));
  const facTtc = pieces.filter((g) => !sousTraitance(g)).reduce((s, g) => s + g.montant, 0);
  // Le prix convenu pas encore facturé coûte, hors TVA estimée, mais sa TVA
  // ne se récupère qu'avec la facture.
  const tvaRecuperable = auCentime(
    pieces.filter((g) => !g.sansFacture).reduce((s, g) => s + tvaDe(g), 0),
  );
  const fac = pieces.filter((g) => !sousTraitance(g)).reduce((s, g) => s + g.montant - tvaDe(g), 0);
  const st = pieces.filter(sousTraitance).reduce((s, g) => s + g.montant - tvaDe(g), 0);
  const mo = (ch.pointages || []).reduce((s, p) => {
    if (p.type === 'absence') return s;
    const emp = list.find((e) => e.id === p.empId);
    return s + hours(p.entree, p.sortie, p.pause) * coutHoraire(emp);
  }, 0);
  const mat = (ch.materiaux || []).reduce((s, m) => s + (+m.pa || 0) * (+m.qte || 0), 0);
  const tauxTva = tauxTvaEnVigueur(soc).normal;
  const tvaFacturee = facturesClients
    ? factureAuClient(ch, facturesClients).tva
    : auCentime(((+ch.facture || 0) * tauxTva) / 100);
  return {
    fac,
    facTtc,
    st,
    mo,
    mat,
    ben: (ch.facture || 0) - fac - st - mo - mat,
    tauxTva,
    tvaFacturee,
    tvaRecuperable,
    tvaDue: auCentime(tvaFacturee - tvaRecuperable),
  };
}

export function formationDu(f) {
  if (!f || !+f.cout || !+f.engagementMois) return 0;
  const st = f.debut ? new Date(f.debut) : f.dateSig ? new Date(f.dateSig) : new Date();
  const n = new Date();
  let m = (n.getFullYear() - st.getFullYear()) * 12 + (n.getMonth() - st.getMonth());
  if (m < 0) m = 0;
  return Math.max(
    0,
    Math.round((+f.cout * Math.max(0, +f.engagementMois - m)) / +f.engagementMois),
  );
}
